import React, { useEffect, useState } from 'react';
import { Justify } from 'tea-component';

import singleSelectFactory from './filters/single-select-factory';
import languages from './filters/languages';
import {
  reviewSelectOptions,
  searchSelectOptions,
  sortByOptions,
  singleLanguageSelectOptions,
  partOfSpeechOptions,
  distanceOptions,
} from './constant';
import { useIntl } from 'umi';
import search from './filters/search';
import download from './filters/download';
import ShowProgress from './filters/show-progress';
import SingleTranslateSelect from './filters/languages/singleLauage';
import AICG from '../Aigc';
import { keywordDistanceTips } from '../BubbleCommon';
import SingleCheckbox from './filters/single-checkbox';
import Metrics from './filters/metrics';
import './index.scss';

type FilterKeysType =
  | 'pos'
  | 'distance'
  | 'languages'
  | 'sortBy'
  | 'review'
  | 'match_model'
  | 'search'
  | 'download'
  | 'language'
  | 'show_progress'
  | 'gpt'
  | 'singleCheckbox'
  | 'metrics';

type FilterBoxItem = {
  key: FilterKeysType | string;
  defaultParams?: any;
  defaultValue?: any;
  className?: any;
  style?: any;
  show?: boolean;
  valueKey?: string;
  element?: JSX.Element | null;
  state?: string;
  disabled?: boolean;
  // 新增事件绑定
  event?: {
    [event: string]: any;
  };
};

type FilterBoxType = {
  left?: (FilterBoxItem | FilterBoxItem[])[];
  right?: (FilterBoxItem | FilterBoxItem[])[];
  onChange: (value: any, key?: FilterKeysType | string) => void;
  className?: string;
  minModal?: boolean;
  /** 下载按钮 loading（下载进行中图标切换为 loading），通常绑定 useDownloadParams 的 downloading */
  downloadLoading?: boolean;
};

export default function FilterBoxCommon({
  left = [],
  right = [],
  onChange,
  className,
  minModal = false,
  downloadLoading = false,
}: FilterBoxType) {
  const { formatMessage } = useIntl();

  const map: any = {
    gpt: {
      render: AICG,
    },
    languages: {
      render: languages,
      defaultValue: ['origin'],
    },
    search: {
      render: search,
      defaultValue: '',
      defaultParams: {
        placeholder: formatMessage({
          id: 'Search for reviews by keywords or phrases.',
        }),
      },
    },
    download: {
      render: download,
    },
    language: {
      render: SingleTranslateSelect,
      defaultValue: 'origin',
      defaultParams: {
        options: singleLanguageSelectOptions,
        defaultBtnText: '',
        btnTextPrefix: '',
        matchButtonWidth: true,
        footer: (
          <div style={{ textAlign: 'left', maxWidth: 204 }}>
            {formatMessage({
              id: 'From GPT-Translator. Powered by DataBrain NLP Team',
            })}
          </div>
        ),
      },
    },
    sortBy: {
      render: singleSelectFactory,
      defaultValue: 'Hot',
      defaultParams: {
        options: sortByOptions,
        defaultBtnText: '',
        btnTextPrefix: `${formatMessage({ id: 'Sort By' })} `,
        matchButtonWidth: true,
      },
    },
    review: {
      render: singleSelectFactory,
      defaultValue: 'posts',
      defaultParams: {
        options: reviewSelectOptions,
        defaultBtnText: 'Posts & Comments',
        btnTextPrefix: 'Review Type:',
        matchButtonWidth: true,
      },
    },
    match_model: {
      render: singleSelectFactory,
      defaultValue: 'exact',
      defaultParams: {
        options: searchSelectOptions(formatMessage),
        footer: (
          <div className="dbo-searchbox-label">
            {formatMessage({
              id: 'Typed text will use precise matching search in preference.',
            })}
          </div>
        ),
        boxClassName: 'dbo-searchbox-dropdown',
        listWidth: 334,
      },
    },
    pos: {
      render: singleSelectFactory,
      defaultValue: 'all',
      defaultParams: {
        options: partOfSpeechOptions,
        defaultBtnText: 'All',
        btnTextPrefix: formatMessage({ id: 'Part-of-speech' }),
        matchButtonWidth: true,
      },
    },
    distance: {
      render: singleSelectFactory,
      defaultValue: 'all',
      defaultParams: {
        options: distanceOptions,
        defaultBtnText: 'All',
        btnTextPrefix: formatMessage({ id: 'Distance' }),
        matchButtonWidth: true,
        tipsMap: keywordDistanceTips,
      },
    },
    show_progress: {
      render: ShowProgress,
      defaultValue: true,
      defaultParams: {
        bubbleParams: {},
      },
    },
    singleCheckbox: {
      render: SingleCheckbox,
      defaultValue: false,
      defaultParams: {},
    },
    metrics: {
      render: Metrics,
      defaultValue: undefined,
      defaultParams: {
        placeholder: '',
        options: [],
      },
    },
  };

  const [value, setValue] = useState({});

  useEffect(() => {
    const curValue: {
      [key: string]: any;
    } = {};

    const notValueKey = ['download', 'element'];

    [...left, ...right]
      .flat()
      .filter((item) => !notValueKey.includes(item.key))
      .forEach((item) => {
        const { key, defaultValue, valueKey } = item;

        const actualKey = valueKey || key;

        if (key) {
          curValue[actualKey] = defaultValue || map?.[key]?.defaultValue || '';
        }
      });

    setValue(curValue);

    onChange?.(curValue);
  }, []);

  const handleOnChange = (key: FilterKeysType, newValue: any) => {
    setValue({
      ...value,
      [key]: newValue,
    });
    if (key === 'search') {
      return;
    }
    onChange?.(
      {
        ...value,
        [key]: newValue,
      },
      key,
    );
  };

  const renderFilters = (
    FilterBoxItems: (FilterBoxItem | FilterBoxItem[])[],
  ) => {
    const result: any = [];

    const renderItem = (item: any) => {
      const {
        key,
        defaultValue,
        defaultParams,
        className,
        style = {},
        show = true,
        valueKey,
        element,
        event = {},
        state,
        disabled,
      } = item;

      if (element)
        return (
          <span
            key={key}
            className={className || ''}
            style={{
              display: show ? 'inline' : 'none',
              verticalAlign: 'middle',
              ...style,
            }}
          >
            {element}
          </span>
        );

      if (!key) return null;

      let { render } = map[key];

      if (!render) return null;

      const actualKey = valueKey || key;

      // 下载按钮：downloadLoading 时图标切换为 loading（父组件用 useDownloadParams().downloading 驱动）
      const downloadState = key === 'download' && downloadLoading ? 'loading' : state;

      if (typeof render === 'function') {
        render = render({
          onChange: handleOnChange,
          key: actualKey,
          defaultValue: defaultValue || map[key].defaultValue,
          defaultParams: {
            ...map[key].defaultParams,
            ...defaultParams,
          },
          event,
          state: downloadState,
          disabled,
        });
      }

      // console.log('social media', key);

      return (
        <span
          key={key + valueKey}
          className={className || ''}
          style={{
            display: show ? 'inline' : 'none',
            verticalAlign: 'middle',
            ...style,
          }}
        >
          {render}
        </span>
      );
    };
    for (let index = 0; index < FilterBoxItems.length; index += 1) {
      const curItem = FilterBoxItems[index];
      if (Array.isArray(curItem)) {
        result.push(
          <div className="dbo-select-searchbox" key={JSON.stringify(curItem)}>
            {curItem.map((item) => renderItem(item))}
          </div>,
        );
      } else {
        result.push(renderItem(curItem));
      }
    }
    return result;
  };

  return (
    <Justify
      className={`dbo-justify ${className} ${minModal ? 'db-min-modal' : ''}`}
      left={<>{renderFilters(left)}</>}
      right={<>{renderFilters(right)}</>}
    />
  );
}
